/**
 * Public front-page USP grid (Epic 20 S20.2, Task 3).
 *
 * "Neljä syytä kokeilla Sagamoonia" — four glass cards, each a swappable Lucide
 * icon (gold-accented, may become a custom illustration later) + Fredoka subhead
 * + body. Responsive 4 / 2×2 / 1. Hardcoded Finnish (Epic 20 i18n exception).
 *
 * @module components/originals/home/UspGrid
 */

import type { LucideIcon } from 'lucide-react';
import { UspOwnHeroIcon, UspTogetherIcon, UspChoiceIcon, UspSafetyIcon } from '../../icons';

interface Usp {
  Icon: LucideIcon;
  heading: string;
  body: string;
}

const USPS: Usp[] = [
  {
    Icon: UspOwnHeroIcon,
    heading: 'Oma hahmosi päähenkilönä. Uusi seikkailu joka ilta.',
    body: 'Lapsen piirroksesta, lemmikistä, pehmolelusta tai muusta kuvasta syntyy oma satuhahmo. Hahmo saa nimen, luonteen ja maailman — ja voi palata uusiin satuihin yhä uudelleen.',
  },
  {
    Icon: UspTogetherIcon,
    heading: 'Vanhempi mukana, ei sivussa.',
    body: 'Sagamoon ei korvaa perinteistä yhteistä satuhetkeä vaan tekee siitä entistä hauskemman ja antaa lapselle aktiivisemman roolin: te teette ja luette sadun yhdessä ja lopuksi saatte keskustelukysymyksiä, joista voitte jutella yhdessä lapsen kanssa.',
  },
  {
    Icon: UspChoiceIcon,
    heading: 'Lapsi pääsee tekemään sadun aikana valintoja.',
    body: 'Sadun aikana lapsi saa tehdä pieniä valintoja: mennäänkö metsään vai luolaan, autetaanko vai paetaanko, uskalletaanko jatkaa vai keksitäänkö toinen tie.',
  },
  {
    Icon: UspSafetyIcon,
    heading: 'Turvallisia satuja lapsen ikään sopivasti.',
    body: 'Sagamoon tekee satuja 3–10-vuotiaille lapsille ikätason mukaan. Pienemmille lempeämmin, isommille seikkailullisemmin — aina turvallisesti ja perheen yhteiseen lukuhetkeen sopiviksi.',
  },
];

export function UspGrid(): JSX.Element {
  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-accent-gold">
          Miksi Sagamoon
        </p>
        <h2 className="mt-2 font-whimsy text-3xl text-text-twilight-cream">
          Neljä syytä kokeilla Sagamoonia.
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {USPS.map(({ Icon, heading, body }) => (
            <article
              key={heading}
              className="flex flex-col rounded-2xl border border-white/10 bg-white/5 p-6"
            >
              <Icon className="h-8 w-8 text-accent-gold" aria-hidden="true" />
              <h3 className="mt-4 font-whimsy text-lg text-text-twilight-cream">{heading}</h3>
              <p className="mt-2 text-sm text-text-twilight-cream/80">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
